import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ReservationForm } from '../components/ReservationForm';
import { useLanguage } from '../context/LanguageContext';
import { 
  Plane, 
  Building2, 
  PartyPopper, 
  Shield, 
  Clock, 
  Star, 
  FileText,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';

// Video de fondo para el hero - ciudad nocturna con autos (motion blur ejecutivo)
const HERO_VIDEO = "https://cdn.coverr.co/videos/coverr-cars-driving-in-the-city-at-night-1764/1080p.mp4";
const HERO_VIDEO_POSTER = "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1920&q=80";

export const HomePage = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Plane,
      title: t('services.airport.title'),
      description: t('services.airport.subtitle'),
      link: '/servicios#aeropuerto',
    },
    {
      icon: Building2,
      title: t('services.corporate.title'),
      description: t('services.corporate.subtitle'),
      link: '/servicios#corporativo',
    },
    {
      icon: PartyPopper,
      title: t('services.events.title'),
      description: t('services.events.subtitle'),
      link: '/servicios#eventos',
    },
  ];

  const features = [
    { icon: Shield, text: t('hero.features.drivers') },
    { icon: Clock, text: t('hero.features.punctuality') },
    { icon: Star, text: t('hero.features.vehicles') },
    { icon: FileText, text: t('hero.features.billing') },
  ];

  const stats = [
    { value: '10+', label: t('stats.experience') },
    { value: '50K+', label: t('stats.trips') },
    { value: '98%', label: t('stats.satisfaction') },
    { value: '24/7', label: t('stats.availability') },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={HERO_VIDEO_POSTER}
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in-up">
              <div className="badge-billing bg-emerald-900/30 border-emerald-700 text-emerald-400">
                <FileText className="w-4 h-4" />
                <span>{t('hero.badge')}</span>
              </div>

              <h1 className="hero-title">
                {t('hero.title')}{' '}
                <span className="text-gradient">{t('hero.titleHighlight')}</span>
              </h1>

              <p className="hero-subtitle max-w-xl">
                {t('hero.subtitle')}
              </p>

              <div className="flex flex-wrap gap-4">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 text-slate-300"
                  >
                    <feature.icon className="w-5 h-5 text-sky-400" />
                    <span className="text-sm">{feature.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto#reservar">
                  <Button 
                    data-testid="hero-cta-reserve"
                    className="btn-accent"
                  >
                    {t('hero.cta')}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/servicios">
                  <Button 
                    data-testid="hero-cta-services"
                    variant="outline"
                    className="btn-secondary"
                  >
                    {t('hero.ctaServices')}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right - Reservation Form */}
            <div className="animate-fade-in-up animation-delay-300 hidden lg:block">
              <ReservationForm variant="glass" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                data-testid={`stat-${index}`}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section data-testid="services-preview" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm tracking-widest uppercase text-sky-600 font-medium mb-4 block">
              {t('services.sectionLabel')}
            </span>
            <h2 className="section-title mb-4">
              {t('services.title')}
            </h2>
            <p className="section-subtitle max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                data-testid={`service-card-${index}`}
                className="card-service group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-sky-500 transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-sky-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-500 leading-relaxed mb-4">
                  {service.description}
                </p>
                <span className="text-sky-600 font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                  {t('services.learnMore')}
                  <ChevronRight className="w-5 h-5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-us-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm tracking-widest uppercase text-sky-600 font-medium mb-4 block">
                {t('whyUs.sectionLabel')}
              </span>
              <h2 className="section-title mb-6">
                {t('whyUs.title')}
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">
                {t('whyUs.subtitle')}
              </p>

              <div className="space-y-4">
                {t('whyUs.features').map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/acerca">
                  <Button 
                    data-testid="btn-about-us"
                    className="btn-primary"
                  >
                    {t('whyUs.cta')}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&w=800&q=80"
                  alt="Servicio Ejecutivo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-6 shadow-xl border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <FileText className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{t('whyUs.billingTitle')}</div>
                    <div className="text-slate-500 text-sm">{t('whyUs.billingSubtitle')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              {t('cta.subtitle')}
            </p>
            <Link to="/contacto#reservar">
              <Button 
                data-testid="cta-reserve"
                className="btn-accent text-lg px-12 py-5"
              >
                {t('hero.cta')}
                <ChevronRight className="w-6 h-6 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile Reservation Form */}
      <section className="lg:hidden section-padding bg-slate-50">
        <div className="container-custom">
          <ReservationForm />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
